import { track } from '@eventbrite/datalayer-library';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { sdkRequest } from '@eventbrite/http';
/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-ignore
import { postAnalyticsAction } from '@eventbrite/site-analytics';
import {
    ANSWERS_KEY,
    COMMERCIAL_PROVEN_DESCRIPTION,
    COMMERCIAL_PROVEN_TITLE,
    COMMERCIAL_UNPROVEN_DESCRIPTION,
    COMMERCIAL_UNPROVEN_TITLE,
    MODAL_COMMERCIAL_PROVEN_DESCRIPTION,
    MODAL_COMMERCIAL_PROVEN_TITLE,
    MODAL_COMMERCIAL_UNPROVEN_DESCRIPTION,
    MODAL_COMMERCIAL_UNPROVEN_TITLE,
    MODAL_NEW_BUSINESS_DESCRIPTION,
    MODAL_NEW_BUSINESS_TITLE,
    NEW_BUSINESS_DESCRIPTION,
    NEW_BUSINESS_TITLE,
    ONBOARDING_ACTION,
    SALES_OPTIONS_EVENT_VALUES,
    VISITOR_OPTIONS_COUNT_VALUES,
} from '../constants';
import {
    Answers,
    CohortCriteria,
    CohortProps,
    CreatorPersonalizationScoreItem,
    CreatorPersonalizationScoreResponse,
    EntryPointProps,
    HeapActionProps,
    SaveSalesEligibleRequest,
} from '../types';

export const trackHeapEvent = (
    action: HeapActionProps,
    entryPoint: EntryPointProps,
    cohort: CohortProps,
) => {
    const eventName = action;
    const eventData = {
        entryPoint,
        cohort,
    };
    track({ eventName, eventData });
};

export const checkLastInteraction = () => {
    const firstSeen = localStorage.getItem('salesEligibleFirstSeen');
    const lastInteraction = localStorage.getItem(
        'salesEligibleLastInteraction',
    );
    const currentTime = Date.now();

    if (!firstSeen) {
        localStorage.setItem('salesEligibleFirstSeen', currentTime.toString());
        return true;
    }

    const timeSinceFirstSeen = currentTime - parseInt(firstSeen, 10);
    const oneWeekInMs = 7 * 24 * 60 * 60 * 1000;
    const fourWeeksInMs = 28 * 24 * 60 * 60 * 1000;

    if (timeSinceFirstSeen >= fourWeeksInMs && !lastInteraction) {
        // If it's been more than four weeks since the first seen, reset salesEligibleFirstSeen
        localStorage.setItem('salesEligibleFirstSeen', currentTime.toString());
        return true;
    }

    if (lastInteraction) {
        const timeSinceLastInteraction =
            currentTime - parseInt(lastInteraction, 10);

        if (timeSinceLastInteraction < oneWeekInMs) {
            // If it's been less than a week since the last interaction, don't show the banner
            return false;
        } else {
            // If it's been more than a week since the last interaction, remove the last interaction
            // and show the banner based on the time since first seen
            localStorage.removeItem('salesEligibleDismissed');
            localStorage.removeItem('salesEligibleLastInteraction');
            return true;
        }
    }

    // If it's been less than one week since the first seen with no interaction,
    // show the banner. Otherwise, don't show the banner
    return timeSinceFirstSeen < oneWeekInMs;
};

const COHORT_CONTENT = {
    'commercial-proven': {
        title: COMMERCIAL_PROVEN_TITLE,
        description: COMMERCIAL_PROVEN_DESCRIPTION,
        modalTitle: MODAL_COMMERCIAL_PROVEN_TITLE,
        modalDescription: MODAL_COMMERCIAL_PROVEN_DESCRIPTION,
    },
    'commercial-unproven': {
        title: COMMERCIAL_UNPROVEN_TITLE,
        description: COMMERCIAL_UNPROVEN_DESCRIPTION,
        modalTitle: MODAL_COMMERCIAL_UNPROVEN_TITLE,
        modalDescription: MODAL_COMMERCIAL_UNPROVEN_DESCRIPTION,
    },
    'new-business': {
        title: NEW_BUSINESS_TITLE,
        description: NEW_BUSINESS_DESCRIPTION,
        modalTitle: MODAL_NEW_BUSINESS_TITLE,
        modalDescription: MODAL_NEW_BUSINESS_DESCRIPTION,
    },
};

export const getCohortContent = (cohort: CohortProps) => {
    const { title, description } = COHORT_CONTENT[cohort];
    return { title, description };
};

export const getCohortModalContent = (cohort: CohortProps) => {
    const { modalTitle: title, modalDescription: description } =
        COHORT_CONTENT[cohort];
    return { title, description };
};

export const isOpportunityColor = (entryPoint: string) => {
    return entryPoint === EntryPointProps.Checkout;
};

export const isNarrow = (entryPoint: string) => {
    return entryPoint === EntryPointProps.CreatorHomeSidebar;
};

export const saveSalesEligibleAnalytics = async ({
    userId,
    phoneNumber,
    firstName,
    lastName,
    email,
    cohort,
    activeOrgId,
}: SaveSalesEligibleRequest) => {
    const dimensions = {
        user_id: userId,
        phone: phoneNumber,
        first_name: firstName,
        last_name: lastName,
        email,
        sales_eligible_cohort: cohort,
        active_organization_id: activeOrgId,
    };

    return postAnalyticsAction(ONBOARDING_ACTION, dimensions);
};

export const getCreatorPersonalizationScore = async () =>
    (await sdkRequest(
        `/api/v3/personalization/scores`,
    )) as Promise<CreatorPersonalizationScoreResponse>;

export const fetchCreatorPersonalizationScore = async () => {
    try {
        const response = await getCreatorPersonalizationScore();
        return getSalesEligibleCohort(response?.items?.[0]);
    } catch (error) {
        console.error('Error fetching creator personalization score:', error);
        return null;
    }
};

export const getCohortData = async (entryPoint: EntryPointProps) => {
    if (entryPoint === EntryPointProps.LoggedOutEventCreate) {
        return shouldShowBannerFromOBAnswers() ? CohortProps.NewBusiness : null;
    } else {
        return fetchCreatorPersonalizationScore();
    }
};

const isCommercialProvenCohort = (
    item: CreatorPersonalizationScoreItem,
    orgAgeInMonths: number,
): boolean => {
    const { gtf, lifetime_event_count, score } = item;

    const criteria: CohortCriteria = {
        orgAgeInMonths: 12,
        lifetime_event_count: 1,
        score: 9,
        gtf: 3500,
    };

    return (
        orgAgeInMonths > criteria.orgAgeInMonths &&
        lifetime_event_count > criteria.lifetime_event_count &&
        score >= criteria.score &&
        gtf > criteria.gtf
    );
};

const isCommercialUnprovenCohort = (
    item: CreatorPersonalizationScoreItem,
    orgAgeInMonths: number,
): boolean => {
    const { gtf, lifetime_event_count, score } = item;

    const criteria: CohortCriteria = {
        orgAgeInMonths: 12,
        lifetime_event_count: 1,
        score: 9,
        gtf: 1000,
    };

    return (
        orgAgeInMonths < criteria.orgAgeInMonths &&
        lifetime_event_count > criteria.lifetime_event_count &&
        score >= criteria.score &&
        gtf > criteria.gtf
    );
};

const isNewBusinessCohort = (
    item: CreatorPersonalizationScoreItem,
    orgAgeInMonths: number,
): boolean => {
    const { gtf, lifetime_event_count, score } = item;

    const criteria: CohortCriteria = {
        orgAgeInMonths: 1,
        lifetime_event_count: 0,
        score: 4,
        gtf: 15,
    };

    return (
        orgAgeInMonths < criteria.orgAgeInMonths &&
        lifetime_event_count === criteria.lifetime_event_count &&
        score > criteria.score &&
        gtf < criteria.gtf
    );
};

const getOrgCreationDate = (): string | null => {
    return HAS_WINDOW
        ? window.__SERVER_DATA__?.user?.activeOrganization?.created
        : null;
};

const calculateOrgAgeInMonths = (orgCreationDate: string | null): number => {
    const orgCreationDateInMs = orgCreationDate
        ? new Date(orgCreationDate).getTime()
        : Date.now();
    return (Date.now() - orgCreationDateInMs) / (1000 * 60 * 60 * 24 * 30);
};

const getSalesEligibleCohort = (
    item: CreatorPersonalizationScoreItem | null | undefined,
): CohortProps | null => {
    if (!item) {
        return null;
    }

    const orgCreationDate = getOrgCreationDate();
    if (!orgCreationDate) {
        return null;
    }

    const orgAgeInMonths = calculateOrgAgeInMonths(orgCreationDate);

    if (isCommercialProvenCohort(item, orgAgeInMonths)) {
        return CohortProps.CommercialProven;
    }

    if (isCommercialUnprovenCohort(item, orgAgeInMonths)) {
        return CohortProps.CommercialUnproven;
    }

    if (isNewBusinessCohort(item, orgAgeInMonths)) {
        return CohortProps.NewBusiness;
    }

    return null;
};

export const shouldShowBannerFromCohort = (
    cohort: CohortProps,
    entryPoint: EntryPointProps,
) => {
    // if the cohort is CommercialProven,
    // show the banner on entry points checkout, creator-home-sidebar and events
    if (cohort === CohortProps.CommercialProven) {
        return [
            EntryPointProps.Checkout,
            EntryPointProps.CreatorHomeSidebar,
            EntryPointProps.Events,
        ].includes(entryPoint);
    }
    // if the cohort is CommercialUnproven,
    // show the banner on entry points checkout, creator-home-main and events
    if (cohort === CohortProps.CommercialUnproven) {
        return [
            EntryPointProps.Checkout,
            EntryPointProps.CreatorHome,
            EntryPointProps.Events,
        ].includes(entryPoint);
    }
    // if the cohort is NewBusiness, show the banner on
    // entry points event-create and checkout
    if (cohort === CohortProps.NewBusiness) {
        return [
            EntryPointProps.LoggedOutEventCreate,
            EntryPointProps.Checkout,
        ].includes(entryPoint);
    }
    return false;
};

const shouldShowBannerFromOBAnswers = () => {
    const rawStoredAnswers = window?.localStorage?.getItem(ANSWERS_KEY);
    const answers = rawStoredAnswers
        ? (JSON.parse(rawStoredAnswers) as Answers)
        : {};

    if (!answers) {
        return false;
    }

    const eventsVolume = answers?.events_volume_pp?.[0]?.value as number;
    const eventAttendees = answers?.event_attendees_pp?.[0]?.value as number;

    if (!eventsVolume || !eventAttendees) {
        return false;
    }

    const isGeneralCategory =
        isGeneralCategoryByEventsVolume(eventsVolume) &&
        isGeneralCategoryByEventAttendees(eventAttendees);

    const isModerateEventsWithLimitedAttendees =
        eventsVolume === SALES_OPTIONS_EVENT_VALUES.FIVE_TO_TEN_EVENTS &&
        isLimitedEventAttendees(eventAttendees);

    const isFrequentEventsWithFewAttendees =
        eventsVolume === SALES_OPTIONS_EVENT_VALUES.TEN_TO_TWENTY_FIVE_EVENTS &&
        eventAttendees === VISITOR_OPTIONS_COUNT_VALUES.UP_TO_TWENTY_FIVE;

    // If the user is in a general category, moderate events with limited attendees,
    // or frequent events with few attendees, don't show the banner
    return !(
        isGeneralCategory ||
        isModerateEventsWithLimitedAttendees ||
        isFrequentEventsWithFewAttendees
    );
};

const isGeneralCategoryByEventsVolume = (eventsVolume: number) =>
    [
        SALES_OPTIONS_EVENT_VALUES.ONE_EVENT,
        SALES_OPTIONS_EVENT_VALUES.TWO_TO_FIVE_EVENTS,
    ].includes(eventsVolume);

const isGeneralCategoryByEventAttendees = (eventAttendees: number) =>
    [
        VISITOR_OPTIONS_COUNT_VALUES.UP_TO_TWENTY_FIVE,
        VISITOR_OPTIONS_COUNT_VALUES.UP_TO_ONE_HUNDRED,
        VISITOR_OPTIONS_COUNT_VALUES.UP_TO_TWO_HUNDRED_AND_FIFTY,
    ].includes(eventAttendees);

const isLimitedEventAttendees = (eventAttendees: number) =>
    [
        VISITOR_OPTIONS_COUNT_VALUES.UP_TO_TWENTY_FIVE,
        VISITOR_OPTIONS_COUNT_VALUES.UP_TO_ONE_HUNDRED,
    ].includes(eventAttendees);
